import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, Popconfirm, notification } from "antd";
import { Link } from "react-router-dom";
import { Row, Form, Input, Card, Col, Select, Collapse } from "antd";

import TopHeader from "../../common/TopHeader/TopHeader";
import Sidebar from "../../common/Sidebar/Sidebar";
import { NotificationOutlined } from "@ant-design/icons";
import QuestionnairesApi from "../../common/Apis/QuestionnairesApi";
import { MinusCircleTwoTone, PlusCircleTwoTone } from "@ant-design/icons";
import AlertsApi from "../../common/Apis/AlertsApi";
import Pagination from "../../common/Pagination/Pagination";
import "../../common/Pagination/Pagination.css";

const Alerts = () => {
    const { Content } = Layout;
    const { Panel } = Collapse;

    // ── Data ────────────────────────────────────────────────
    const [Alerts, setAlerts] = React.useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // ── Pagination ──────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [formPaginate] = Form.useForm();
    formPaginate.setFieldsValue({ paginate: pageSize });

    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));

    // ── Fetch list ──────────────────────────────────────────
    const getAlerts = useCallback(() => {
        AlertsApi.ListAlerts()
            .then((response) => {
                const list = response.payloadDto || [];
                setAlerts(list);
                setTotalPages(Math.ceil(list.length / pageSize));
                setTotalElements(list.length);
                setCurrentPage(1);
            })
            .catch((err) => {
                console.log(err);
            });
    }, [pageSize]);

    const [isLoading, setIsLoading] = useState(false);
    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 800);
    }, []);

    useEffect(() => {
        PageLoading();
        getAlerts();
    }, [getAlerts, PageLoading]);

    // ── Delete ──────────────────────────────────────────────
    const handleDeleteAlert = (AlertID) => {
        AlertsApi.DeleteAlerts(AlertID)
            .then(() => {
                notification.open({
                    message: <span style={{ fontWeight: "bold" }}>Deleted</span>,
                    description: "Delete Successfully",
                    closeIcon: false,
                    duration: 3,
                    style: { backgroundColor: "#b3ffb3", color: "green" },
                });
                PageLoading();
                getAlerts();
            })
            .catch((err) => {
                if (err.apiError?.status) {
                    notification.open({
                        message: <span style={{ fontWeight: "bold" }}>Error</span>,
                        description: err.apiError.message,
                        closeIcon: false,
                        duration: 3,
                        style: { backgroundColor: "#fff2f0", color: "red" },
                    });
                }
            });
    };

    // ── Pagination handlers ─────────────────────────────────
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (value) => {
        setPageSize(value);
        setCurrentPage(1);
        formPaginate.setFieldsValue({ paginate: value });
        setTotalPages(Math.ceil(Alerts.length / value));
    };

    // ── Filters ─────────────────────────────────────────────
    const handleRemovefilterDepartment = (FilterDepartmentIndex) => {
        const newfilterDepartment = [...filterDepartment];
        newfilterDepartment.splice(FilterDepartmentIndex, 1);
        setFilterDepartments(newfilterDepartment);
        if (FilterDepartmentIndex === 0) {
            getAlerts();
        }
    };

    const handleClear = () => {
        getAlerts();
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 800);
        setFilterDepartments([{ title: "", answerType: "", name: "" }]);
    };

    const formRef = useRef(null);
    const [filterDepartment, setFilterDepartments] = useState([
        { title: "", answerType: "", name: "" },
    ]);

    const handleAddfilterDepartments = () => {
        setFilterDepartments([...filterDepartment, { title: "", answerType: "", name: "" }]);
    };

    const handleSubmit = (data) => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 800);

        let singleLineString = filterDepartment
            .map((obj) => {
                if (obj.answerType === "%") {
                    return `${obj.title}=${obj.answerType}${obj.name}${obj.answerType}`;
                } else if (obj.answerType === "=") {
                    return `${obj.title}${obj.answerType}${obj.name}`;
                } else {
                    return `${obj.title}=${obj.name}`;
                }
            })
            .join("&");

        let filderData = window.btoa(
            "componentCode=ALERT&subTenant.id=" + decodedToken?.subTenantId + "&" + singleLineString
        );

        QuestionnairesApi.FilterQuestionsetNext(filderData, 0, pageSize)
            .then((response) => {
                const filtered = response.payloadDto || [];
                setAlerts(filtered);
                setTotalPages(Math.ceil(filtered.length / pageSize));
                setTotalElements(filtered.length);
                setCurrentPage(1);            // ← reset to first page
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleChangeTitle = (i, value) => {
        const next = [...filterDepartment];
        next[i].title = value;
        setFilterDepartments(next);
    };
    const handleChangeAnswertype = (i, value) => {
        const next = [...filterDepartment];
        next[i].answerType = value;
        setFilterDepartments(next);
    };
    const handleChangename = (i, value) => {
        const next = [...filterDepartment];
        next[i].name = value;
        setFilterDepartments(next);
    };

    // ── Render ──────────────────────────────────────────────
    const paginatedData = Alerts.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const renderTableRows = () => {
        if (paginatedData.length > 0) {
            return paginatedData.map((item) => (
                <tr key={item.id} className="text-center">
                    <td style={{ width: "22%" }} className="pt-3">
                        {item?.productDTO?.productName}
                    </td>
                    <td style={{ width: "15%" }} className="pt-3">
                        {item?.alertType}
                    </td>
                    <td style={{ width: "15%" }} className="pt-3">
                        {item?.severity}
                    </td>
                    <td style={{ width: "22%" }} className="pt-3">
                        {item?.description}
                    </td>
                </tr>
            ));
        } else {
            return (
                <tr className="text-center EmptyTable">
                    <td colSpan={4} className="text-center ">
                        <img
                            src="/assest/Images/emptyIcon.svg"
                            className="logo-png mt-3"
                            style={{ width: "10%" }}
                            alt=""
                        />
                        <br />
                        <Button
                            className={decodedToken?.permissions?.some(
                                (permission) => permission?.authority === "ADD_ALERT"
                            ) ? "btn btn-primary btn-save m-0 my-4" : "d-none"}
                            href={"/masters/alerts/add-alert"}
                        >
                            Add new
                        </Button>
                    </td>
                </tr>
            );
        }
    };

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}
                <div style={{ padding: "20px", paddingBottom: "5px" }}>
                    <Form
                        ref={formRef}
                        name="nest-messages"
                        layout="vertical"
                        className="FormClass FilterForm"
                        onFinish={handleSubmit}
                    >
                        <Collapse>
                            <Panel
                                header={
                                    <h5 className="mb-0">
                                        <b>Filter</b>
                                    </h5>
                                }
                                key="1"
                            >
                                {filterDepartment.map((input, filterDepartmentIndex) => (
                                    <div key={filterDepartmentIndex}>
                                        <Row gutter={12} className="pt-2">
                                            <Col span={5}>
                                                <Form.Item
                                                    name={`title${filterDepartmentIndex}`}
                                                    className="text-lable"
                                                    rules={[{ required: true, message: "Please Select" }]}
                                                >
                                                    <Select
                                                        className="control-Select"
                                                        placeholder="Filter By"
                                                        value={input.title}
                                                        onChange={(value) =>
                                                            handleChangeTitle(filterDepartmentIndex, value)
                                                        }
                                                        options={[
                                                            { value: "severity", label: "Severity" },
                                                            { value: "alertType", label: "Alert Type" },
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col span={4}>
                                                <Form.Item
                                                    name={`type${filterDepartmentIndex}`}
                                                    className="text-lable"
                                                    rules={[{ required: true, message: "Please Select type." }]}
                                                >
                                                    <Select
                                                        className="control-Select mb-1"
                                                        placeholder="Condition"
                                                        onChange={(value) =>
                                                            handleChangeAnswertype(filterDepartmentIndex, value)
                                                        }
                                                        options={[
                                                            { value: "%", label: "Matches with" },
                                                            { value: "=", label: "Equals to" },
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col span={5}>
                                                <Form.Item
                                                    name={`name${filterDepartmentIndex}`}
                                                    className="text-lable"
                                                    rules={[{ required: true, message: "Please enter Text." }]}
                                                >
                                                    <Input
                                                        type="text"
                                                        onChange={(e) =>
                                                            handleChangename(filterDepartmentIndex, e.target.value)
                                                        }
                                                        placeholder="Text"
                                                        className="control-input"
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col span={8}>
                                                <Form.Item className="text-lable">
                                                    {filterDepartmentIndex === 0 ? (
                                                        <Row gutter={23} className="mb-3">
                                                            <Col span={2} className="mx-0">
                                                                <Form.Item className="text-lable">
                                                                    <PlusCircleTwoTone
                                                                        onClick={handleAddfilterDepartments}
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={10}>
                                                                <Button
                                                                    type="primary"
                                                                    htmlType="submit"
                                                                    className="btn btn-primary btn-save table-action icon-center "
                                                                >
                                                                    Filter
                                                                </Button>
                                                            </Col>
                                                            <Col span={10}>
                                                                <Button
                                                                    type="reset"
                                                                    htmlType="reset"
                                                                    onClick={() => handleClear()}
                                                                    style={{ color: "#0b5ed7" }}
                                                                    className="btn btn-primary btn-back table-action icon-center"
                                                                >
                                                                    Clear
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    ) : (
                                                        <MinusCircleTwoTone
                                                            onClick={() =>
                                                                handleRemovefilterDepartment(filterDepartmentIndex)
                                                            }
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                            </Panel>
                        </Collapse>
                    </Form>
                </div>

                <Content
                    className="form-content nav-content"
                    style={{ margin: "14px 16px 0", padding: "30px", background: "white" }}
                >
                    <div>
                        {/* Toolbar: Add new + Page size */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            {paginatedData?.length > 0 && (
                                <Button
                                    className={decodedToken?.permissions?.some(
                                        (permission) => permission?.authority === "ADD_ALERT"
                                    ) ? "btn btn-primary btn-save m-0 my-2" : "d-none"}
                                    href={"/masters/alerts/add-alert"}
                                >
                                    Add new
                                </Button>
                            )}
                            <Form form={formPaginate}>
                                <Form.Item
                                    name="paginate"
                                    className="text-lable"
                                    style={{ width: "100px", marginBottom: 0 }}
                                >
                                    <Select
                                        className="control-Select"
                                        onChange={(value) => handlePageSizeChange(value)}
                                        options={[
                                            { value: 10, label: "10" },
                                            { value: 20, label: "20" },
                                            { value: 30, label: "30" },
                                            { value: 40, label: "40" },
                                            { value: 50, label: "50" },
                                            { value: 100, label: "100" },
                                        ]}
                                    />
                                </Form.Item>
                            </Form>
                        </div>

                        <Row>
                            <Col lg={6} md={10} className="pt-3">
                                <Link to={"/users"}>
                                    <Card
                                        style={{
                                            background:
                                                "linear-gradient(to right, rgb(177 195 238), rgb(143 179 242))",
                                        }}
                                    >
                                        <Card.Meta
                                            avatar={<NotificationOutlined style={{ fontSize: "24px" }} />}
                                            title="Total Alerts"
                                            description={
                                                <span
                                                    style={{
                                                        fontSize: "20px",
                                                        fontWeight: "bold",
                                                        color: "black",
                                                    }}
                                                >
                                                    {totalElements}
                                                </span>
                                            }
                                        />
                                    </Card>
                                </Link>
                            </Col>
                        </Row>

                        <table className="table users-table">
                            <thead>
                                <tr>
                                    <th>Product Name</th>
                                    <th>Alert Type</th>
                                    <th>Severity</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>{renderTableRows()}</tbody>
                        </table>

                        {paginatedData?.length > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                maxVisiblePages={5}
                                showPageInfo={true}
                                totalItems={totalElements}
                                pageSize={pageSize}
                            />
                        )}
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default Alerts;